import React from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  alpha,
  Chip,
} from "@mui/material";
import ContentCutOutlined from "@mui/icons-material/ContentCutOutlined";
import AutoStoriesOutlined from "@mui/icons-material/AutoStoriesOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import ScheduleSendOutlined from "@mui/icons-material/ScheduleSendOutlined";
import { useCustomTheme } from "../../utils/useCustomTheme";
import { TranslateContext } from "../../context/TranslateContext";

const USE_CASE_ICONS = {
  clipping: ContentCutOutlined,
  stories: AutoStoriesOutlined,
  ads: CampaignOutlined,
  studio: VideocamOutlined,
  ideas: LightbulbOutlined,
  social: ScheduleSendOutlined,
};

function UseCaseCard({ iconKey, tag, title, description, accent, isDark, config }) {
  const Icon = USE_CASE_ICONS[iconKey] || VideocamOutlined;
  const border = isDark ? config.border_dark : config.border_light;
  const paper = isDark ? config.background_paper_dark : config.background_paper_light;

  return (
    <Box
      sx={{
        height: "100%",
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        border: `1px solid ${border}`,
        bgcolor: paper,
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: isDark ? "0 24px 48px rgba(0,0,0,0.45)" : "0 24px 48px rgba(0,0,0,0.1)",
          borderColor: alpha(accent, 0.45),
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha(accent, 0.22)} 0%, ${alpha(accent, 0.06)} 100%)`,
            border: `1px solid ${alpha(accent, 0.28)}`,
          }}
        >
          <Icon sx={{ color: accent, fontSize: 26 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {tag && (
            <Chip
              label={tag}
              size="small"
              sx={{
                mb: 1,
                height: 22,
                fontWeight: 700,
                fontSize: "0.65rem",
                bgcolor: alpha(accent, 0.1),
                color: accent,
                border: `1px solid ${alpha(accent, 0.2)}`,
              }}
            />
          )}
          <Typography
            variant="h6"
            fontWeight={800}
            gutterBottom
            sx={{ fontFamily: config.font_family, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7, fontFamily: config.font_family }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function MonetizeSection() {
  const { lang } = React.useContext(TranslateContext);
  const { config, isDark } = useCustomTheme();
  const accent = isDark ? config.primary_dark || config.primary : config.primary;

  const useCases = [
    {
      icon: "clipping",
      tag: lang?.homeMonetizeClippingTag || "Earn online",
      title: lang?.homeMonetizeClippingTitle || "AI Clipping",
      desc:
        lang?.homeMonetizeClippingDesc ||
        "Turn podcasts and long videos into viral Shorts. Grow theme pages, stack creator-fund payouts, and sell clipping services — one long video becomes dozens of posts.",
    },
    {
      icon: "stories",
      tag: lang?.homeMonetizeStoriesTag || "Narrative video",
      title: lang?.homeMonetizeStoriesTitle || "AI Stories",
      desc:
        lang?.homeMonetizeStoriesDesc ||
        "Script-to-video story arcs with voiceover and captions. Build faceless channels, educational series, and binge-worthy series that keep viewers watching.",
    },
    {
      icon: "ads",
      tag: lang?.homeMonetizeAdsTag || "High ROAS",
      title: lang?.homeMonetizeAdsTitle || "Ads that convert",
      desc:
        lang?.homeMonetizeAdsDesc ||
        "UGC-style product ads with AI creators holding your SKU. Hooks, demos, and CTAs engineered for TikTok Shop, Meta, and marketplace listings.",
    },
    {
      icon: "studio",
      tag: lang?.homeMonetizeStudioTag || "Pro video",
      title: lang?.homeMonetizeStudioTitle || "Video Studio",
      desc:
        lang?.homeMonetizeStudioDesc ||
        "Text, image, and video-to-video with Kling and leading models. Motion sync, music-driven visuals, and cinematic product shots — no timeline editing.",
    },
    {
      icon: "ideas",
      tag: lang?.homeMonetizeIdeasTag || "Templates",
      title: lang?.homeMonetizeIdeasTitle || "Explore Ideas",
      desc:
        lang?.homeMonetizeIdeasDesc ||
        "Browse proven viral ad templates, recreate winning formats with your product and character, and ship faster than your competition.",
    },
    {
      icon: "social",
      tag: lang?.homeMonetizeSocialTag || "Publish",
      title: lang?.homeMonetizeSocialTitle || "Schedule & share",
      desc:
        lang?.homeMonetizeSocialDesc ||
        "Queue clips and ads to TikTok, Instagram, YouTube, and Facebook. AI captions, multi-platform posting, and a library that stores every asset.",
    },
  ];

  return (
    <Box
      component="section"
      id="use-cases"
      sx={{
        py: { xs: 5, md: 7 },
        bgcolor: isDark ? config.background_paper_dark : config.background_paper_light,
        borderTop: `1px solid ${isDark ? config.border_dark : config.border_light}`,
        borderBottom: `1px solid ${isDark ? config.border_dark : config.border_light}`,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={1.5} textAlign="center" mb={6} maxWidth={720} mx="auto">
          <Typography
            variant="overline"
            fontWeight={700}
            letterSpacing="0.12em"
            color="text.secondary"
          >
            {lang?.homeMonetizeBadge || "Built to grow & earn"}
          </Typography>
          <Typography
            component="h2"
            variant="h3"
            fontWeight={800}
            letterSpacing="-0.03em"
            sx={{ fontFamily: config.font_family }}
          >
            {lang?.homeMonetizeTitle || "Create content people"}{" "}
            <Box component="span" sx={{ color: accent }}>
              {lang?.homeMonetizeTitleAccent || "pay to watch"}
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
            {lang?.homeMonetizeSubtitle ||
              "Creators and brands use Beyund AI to clip long-form video for income, ship story-driven channels, and run ads that actually convert — all from one studio."}
          </Typography>
        </Stack>

        <Grid container spacing={2.5}>
          {useCases.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.icon}>
              <UseCaseCard
                iconKey={item.icon}
                tag={item.tag}
                title={item.title}
                description={item.desc}
                accent={accent}
                isDark={isDark}
                config={config}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
